import { redirect } from "@tanstack/react-router";
import type { JwtPayload } from "../api/types";
import {
  hasModuleAccess,
  hasPermissionInPayload,
  hasRoleInPayload,
  readCachedNavigation,
} from "./permissions";
import { isSignupOnboarding } from "./signup-onboarding";
import {
  capturePendingExternalRedirect,
  returnAfterAuth,
} from "./external-redirect";
import {
  bounceToAccountLogin,
  haltForExternalNavigation,
  isAccountAppHost,
} from "./app-origins";
import { getAuthCookie } from "./session-cookie";
import { decodeAccessTokenPayload } from "./jwt";
import {
  MODULE_WORKSPACE,
  MODULE_FLEET_MANAGEMENT,
  MODULE_SITE_MANAGEMENT,
  MODULE_SYSTEM,
  MODULE_CERTIFICATION,
} from "../navigation/constants";

function readAccessToken(): string | null {
  return getAuthCookie("access_token");
}

function readJwtPayload(): JwtPayload | null {
  return decodeAccessTokenPayload(readAccessToken());
}

function hasRefreshToken(): boolean {
  return Boolean(getAuthCookie("refresh_token"));
}

/**
 * True only on a tab where the nav-permission cache has never been written
 * at all (sessionStorage is per-tab — a fresh tab/deep link/bookmark starts
 * empty even with a perfectly valid session, until the async profile
 * hydrate finishes). Distinct from "populated but genuinely zero modules."
 */
function navCacheNeverPopulated(): boolean {
  return readCachedNavigation() === null;
}

/**
 * A permission/module guard failed, but only because this tab's cache
 * hasn't warmed yet, not because access was actually denied — send the user
 * to wait for hydrate and retry, instead of silently bouncing to the "/"
 * dashboard stub as if they had no access.
 */
function redirectToPendingHydrate(): never {
  const target =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "/";
  throw redirect({ to: "/redirecting", search: { redirect: target } });
}

/**
 * Before load function to protect routes that require authentication.
 * On product apps, unauthenticated visitors are bounced to account-web.
 * On account-web itself, they are sent to the local /login route.
 */
export function requireAuth() {
  return () => {
    const token = readAccessToken();
    const payload = readJwtPayload();
    // Missing token, or expired/invalid JWT with no refresh token to recover.
    const sessionUnrecoverable = !token || (!payload && !hasRefreshToken());
    if (sessionUnrecoverable) {
      if (typeof window !== "undefined" && !isAccountAppHost()) {
        bounceToAccountLogin();
        // Do not throw redirect() here — that re-enters the router and
        // spams History APIs (Firefox: "The operation is insecure").
        haltForExternalNavigation();
      }
      throw redirect({
        to: "/login",
        search: {
          redirect: typeof window !== "undefined" ? window.location.href : "/user-setup",
        },
      });
    }
  };
}

export function requireGuest() {
  return () => {
    // Capture a cross-app redirect target (e.g. certification or pro bouncing
    // an unauthenticated visitor to account-web) regardless of auth state —
    // it needs to survive the whole login/registration flow, which is why
    // it's persisted to sessionStorage rather than only read once here.
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      capturePendingExternalRedirect(params.get("redirect") ?? undefined);
    }

    const accessToken = readAccessToken();

    if (accessToken && !isSignupOnboarding()) {
      // Already signed in — return to the originating product app (or pro home).
      if (returnAfterAuth()) return;
      throw redirect({
        to: "/",
      });
    }
  };
}

export function requirePermission(permission: string) {
  return () => {
    requireAuth()();
    const payload = readJwtPayload();
    if (!hasPermissionInPayload(payload, permission)) {
      if (payload && navCacheNeverPopulated()) redirectToPendingHydrate();
      throw redirect({ to: "/" });
    }
  };
}

/**
 * Require access to a Lion Pro module (dropdown collection).
 * Only apply to routes in the navigation catalog — not public or auth-only routes.
 */
export function requireModuleAccess(moduleSlug: string) {
  return () => {
    requireAuth()();
    const payload = readJwtPayload();
    if (hasRoleInPayload(payload, "super-admin")) {
      return;
    }
    if (hasModuleAccess(moduleSlug)) {
      return;
    }
    // A brand-new tab/deep link starts with an empty (never-written)
    // sessionStorage cache even for a perfectly valid session — that's a
    // race, not a denial. Wait for hydrate instead of bouncing to "/".
    if (payload && navCacheNeverPopulated()) redirectToPendingHydrate();
    throw redirect({ to: "/" });
  };
}

export function requireModuleAndPermission(moduleSlug: string, permission: string) {
  return () => {
    requireModuleAccess(moduleSlug)();
    requirePermission(permission)();
  };
}

/** Restrict a route to super-admin users only. */
export function requireSuperAdmin() {
  return () => {
    requireAuth()();
    const payload = readJwtPayload();
    if (!hasRoleInPayload(payload, "super-admin")) {
      throw redirect({ to: "/" });
    }
  };
}

/** Module slugs for catalog route guards. */
export {
  MODULE_WORKSPACE,
  MODULE_SITE_MANAGEMENT,
  MODULE_FLEET_MANAGEMENT,
  MODULE_SYSTEM,
  MODULE_CERTIFICATION,
};
